import { z } from 'zod';
import { NodeMessageRoleSchema, NodeMessageSchema } from './event.js';
/**
 * Run-trace read contracts — the shapes behind `GET /v1/runs/:id/structure`,
 * `/node-detail` and `/search`, and behind the `get_run_structure`,
 * `get_node_detail`, `search_run_content` MCP tools.
 *
 * Two consumers, one data layer: the console renders a person the full node
 * thread, while the MCP layer caps what an agent can pull into its own
 * context. The cap lives at the tool boundary, not here — everything in this
 * file is available at full fidelity to the console.
 */
// ─── Structure ───────────────────────────────────────────────────────────────
/**
 * Per-node skeleton row. Deliberately carries no `input`/`output`/`trace`
 * payload — `contentChars` is the sizing hint that tells a caller whether to
 * fetch the node whole or search inside it.
 */
export const RunStructureNodeSchema = z.object({
    nodeExecutionId: z.string(),
    nodeKey: z.string().nullable(),
    nodeType: z.string().nullable(),
    /** Execution order within the run (index into the executor's `plan.order`). */
    order: z.number().int(),
    status: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
    /** Decimal serialized as a string so no precision is lost over the wire. */
    costUsd: z.string().nullable(),
    /**
     * True for nodes whose content is a model context window. `nodeType ===
     * 'llm-agent'` OR `tokensIn > 0`, so a future node type that calls a model
     * is classified correctly without a code change here.
     */
    isLlmNode: z.boolean(),
    /** `length(trace::text)` — the whole trace blob, not just message content. */
    contentChars: z.number().int(),
    messageCount: z.number().int(),
    /**
     * Short (<= 200 char) summary. The cached generated headline when one
     * exists, otherwise the executor's mechanical `trace.summary`. Never
     * triggers generation — structure is always a cheap call.
     */
    summary: z.string().nullable(),
    toolInvocationCount: z.number().int(),
    error: z.unknown().nullable(),
});
export const RunStructureRunSchema = z.object({
    id: z.string(),
    workflowId: z.string(),
    workflowName: z.string().nullable(),
    workflowVersionId: z.string(),
    status: z.string(),
    trigger: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    totalCostUsd: z.string().nullable(),
    totalTokensIn: z.number().int(),
    totalTokensOut: z.number().int(),
    abortReason: z.string().nullable(),
    input: z.unknown().nullable(),
    output: z.unknown().nullable(),
    error: z.unknown().nullable(),
});
export const RunStructureSchema = z.object({
    run: RunStructureRunSchema,
    nodes: z.array(RunStructureNodeSchema),
});
// ─── Node summaries ──────────────────────────────────────────────────────────
/**
 * Bumped whenever the generation prompt or output shape changes. A cached row
 * at an older version is regenerated on next access, which is what buys us
 * prompt iteration without a backfill job.
 */
// v2: `toolCalls` tallies are derived from `trace.messages` when a node has
// no `ToolInvocation` rows (llm-agent nodes never write that table) — v1 rows
// cached an empty tally for exactly those nodes, so they must regenerate.
export const nodeSummaryVersion = 2;
export const NodeSummaryToolCallSchema = z.object({
    name: z.string(),
    count: z.number().int(),
    /**
     * 'ok' | 'error' | 'mixed' — from recorded ToolInvocation rows when the
     * node has them (mcp-query), else derived from `trace.messages` (llm-agent
     * nodes, whose tool loop never writes the table).
     */
    outcome: z.string(),
});
export const NodeSummarySchema = z.object({
    headline: z.string(),
    narrative: z.string(),
    toolCalls: z.array(NodeSummaryToolCallSchema),
    keyFindings: z.array(z.string()),
    /**
     * False when the LLM call failed and we fell back to a mechanical summary
     * (role counts, tool names, first/last excerpts). A degraded summary is
     * still served — it is more useful to the caller than an error.
     */
    generated: z.boolean(),
    model: z.string().nullable(),
    summaryVersion: z.number().int(),
    generatedAt: z.string(),
});
// ─── Node detail ─────────────────────────────────────────────────────────────
export const NodeToolInvocationSchema = z.object({
    id: z.string(),
    toolName: z.string(),
    input: z.unknown().nullable(),
    output: z.unknown().nullable(),
    error: z.unknown().nullable(),
    latencyMs: z.number().int().nullable(),
    createdAt: z.string(),
});
export const NodeDetailSchema = z.object({
    runId: z.string(),
    nodeExecutionId: z.string(),
    nodeKey: z.string().nullable(),
    nodeType: z.string().nullable(),
    status: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    tokensIn: z.number().int(),
    tokensOut: z.number().int(),
    costUsd: z.string().nullable(),
    isLlmNode: z.boolean(),
    contentChars: z.number().int(),
    messageCount: z.number().int(),
    input: z.unknown().nullable(),
    output: z.unknown().nullable(),
    error: z.unknown().nullable(),
    /** Present only when `include` asked for messages. Null otherwise. */
    messages: z.array(NodeMessageSchema).nullable(),
    /** Present only when `include` asked for messages. Null otherwise. */
    toolInvocations: z.array(NodeToolInvocationSchema).nullable(),
    /** Present only when `include` asked for a summary. Null otherwise. */
    summary: NodeSummarySchema.nullable(),
});
export const NodeDetailIncludeSchema = z.enum(['messages', 'summary']);
/**
 * Query contract for the node-detail endpoint. Exactly one of
 * `nodeExecutionId` / `nodeKey` — `runId` comes from the path, and every
 * lookup resolves the run first so a `nodeExecutionId` from another org can
 * never be read (NodeExecution has no `orgId` of its own).
 */
export const NodeDetailQuerySchema = z
    .object({
    nodeExecutionId: z.string().optional(),
    nodeKey: z.string().optional(),
    include: z.array(NodeDetailIncludeSchema).default(['messages']),
})
    .refine((v) => Boolean(v.nodeExecutionId) !== Boolean(v.nodeKey), {
    message: 'Provide exactly one of `nodeExecutionId` or `nodeKey`',
});
// ─── Search ──────────────────────────────────────────────────────────────────
export const RunSearchMatchSchema = z.object({
    nodeExecutionId: z.string(),
    nodeKey: z.string().nullable(),
    /** Zero-based index into that node execution's `trace.messages`. */
    messageIndex: z.number().int(),
    messageId: z.string().nullable(),
    role: z.string().nullable(),
    name: z.string().nullable(),
    toolCallId: z.string().nullable(),
    createdAt: z.string().nullable(),
    /** `contextChars/2` either side of the hit, so response size is bounded. */
    excerpt: z.string(),
    /** Offset of the match within the excerpt. */
    matchOffset: z.number().int(),
});
export const RunSearchResultSchema = z.object({
    runId: z.string(),
    query: z.string(),
    totalMatches: z.number().int(),
    returned: z.number().int(),
    truncated: z.boolean(),
    matches: z.array(RunSearchMatchSchema),
});
export const RunSearchQuerySchema = z.object({
    /**
     * Literal substring, case-insensitive. NOT semantic — `%` and `_` are
     * escaped before they reach the `ILIKE`, so the query means exactly what
     * it says.
     */
    query: z.string().min(2).max(200),
    nodeExecutionId: z.string().optional(),
    roles: z.array(NodeMessageRoleSchema).optional(),
    contextChars: z.number().int().min(0).max(2000).default(400),
    limit: z.number().int().positive().max(50).default(10),
});
/**
 * `get_node_detail(mode: 'auto')` returns full content only below this many
 * characters. Above it — and for any LLM-agent node regardless of size — the
 * agent gets a summary and a pointer to `search_run_content`. This is the
 * context budget, and it is enforced at the MCP boundary only: the console
 * reads the same rows at full fidelity.
 */
export const nodeDetailAutoFullMaxChars = 8000;
//# sourceMappingURL=run-trace.js.map